/**
 * KycCategoryRadioGroup - KYC 分类单选组组件
 *
 * @description
 * 专为 KYC 流程设计的分类单选组组件，支持多个分类，每个分类下有独立的选项列表。
 * 每个分类内单选，value 为对象类型 { [categoryKey: string]: string }
 *
 * @usage
 * ```tsx
 * // 基础用法
 * <KycCategoryRadioGroup
 *   label="投资经验"
 *   value={{
 *     "Fund Products": "1-10",
 *     "Equities": "10-20"
 *   }}
 *   categories={[
 *     {
 *       key: "Fund Products",
 *       options: [
 *         { value: '1-10', title: '1 - 10' },
 *         { value: '10-20', title: '10 - 20' },
 *         { value: '20+', title: '20 +' },
 *         { value: 'none', title: 'None' },
 *       ]
 *     },
 *     {
 *       key: "Equities",
 *       options: [
 *         { value: '1-10', title: '1 - 10' },
 *         { value: '10-20', title: '10 - 20' },
 *       ]
 *     }
 *   ]}
 *   onValueChange={(value) => setValue(value)}
 * />
 * ```
 */

import React, { memo, useCallback } from "react";
import { StyleSheet, Text, View } from "../platformComponents";
import type { IKycCategoryRadioGroupProps } from "@adgm/cross-models";
import { colors, fontFamily, fontSize, margin, padding, flexDirection } from "../styles/common";
import { AsFormItemLayout } from "./AsFormItemLayout";
import { IS_WEB } from "@adgm/libs";
import { CommonRadio } from "../common";
import { KycLabelElement } from "./KycLabelElement";
import { useGenericFormContext } from "./GenericForm";

/**
 * KycCategoryRadioGroup - 分类单选组组件
 *
 * @param props 组件属性配置
 * @returns 分类单选组组件 JSX 元素
 */
export const KycCategoryRadioGroup = memo((props: IKycCategoryRadioGroupProps) => {
  const {
    value = null,
    label,
    error,
    categories,
    sequenceNumber,
    tip,
    hideErrorSpace,
    onValueChange,
    required,
    optionGap = 100,
  } = props;

  const { genericFormType } = useGenericFormContext();
  const isQuestionnaireForm = genericFormType === "questionnaire";

  /**
   * 处理某个分类下的选项变化
   * @param categoryKey 分类 key
   * @param optionValue 选中的选项值
   */
  const handleCategoryOptionChange = useCallback(
    (categoryKey: string, optionValue: string) => {
      const currentValue = value ?? {};
      const newValue = {
        ...currentValue,
        [categoryKey]: optionValue,
      };
      onValueChange?.(newValue);
    },
    [value, onValueChange],
  );

  return (
    <AsFormItemLayout
      {...props}
      error={error}
      tip={tip}
      style={[styles.label]}
      hideErrorSpace={hideErrorSpace}
      containerStyle={{
        backgroundColor: colors.transparent,
      }}
      labelElement={
        <KycLabelElement label={label} sequenceNumber={sequenceNumber} required={required} />
      }>
      <View style={[styles.container, { paddingLeft: isQuestionnaireForm ? padding.p40 : 0 }]}>
        {categories.map((category, categoryIndex) => {
          const categoryKey = category.key;
          const categorySelectedValue = value?.[categoryKey] || "";

          return (
            <View
              key={categoryKey}
              style={[
                styles.categoryContainer,
                categoryIndex === categories.length - 1 ? {} : styles.categorySpacing,
              ]}>
              {/* 分类标题 */}
              <Text style={styles.categoryTitle}>{category.key}</Text>

              {/* 该分类下的选项列表 */}
              <View
                style={
                  IS_WEB
                    ? {
                        flexDirection: flexDirection.row,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        columnGap: optionGap,
                        rowGap: 20,
                      }
                    : {
                        flexDirection: flexDirection.column,
                      }
                }>
                {category.options.map((option, optionIndex) => {
                  const isChecked = categorySelectedValue === option.value;

                  let containerStyle = {};
                  if (IS_WEB) {
                    containerStyle = {
                      flexShrink: 0,
                    };
                  } else if (optionIndex < category.options.length - 1) {
                    containerStyle = styles.optionContainerMobile;
                  }

                  return (
                    <View key={`${categoryKey}-${option.value}`} style={containerStyle}>
                      <CommonRadio
                        value={option.value}
                        checked={isChecked}
                        label={option.title}
                        onChange={val => handleCategoryOptionChange(categoryKey, val)}
                      />
                    </View>
                  );
                })}
              </View>
            </View>
          );
        })}
      </View>
    </AsFormItemLayout>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginBottom: -margin.m24,
  },
  categoryContainer: {
    flex: 1,
    marginBottom: padding.p24,
  },
  categorySpacing: {
    marginBottom: padding.p32,
  },
  categoryTitle: {
    fontSize: IS_WEB ? fontSize.f16 : fontSize.f14,
    fontFamily: IS_WEB ? "Common-Medium" : fontFamily.commonRegular,
    color: colors.white,
    marginBottom: margin.m16,
  },

  optionContainerWeb: {
    marginRight: padding.p24,
  },
  optionContainerMobile: {
    paddingBottom: padding.p24,
    alignItems: "stretch",
  },
  label: {
    fontSize: IS_WEB ? fontSize.f20 : fontSize.f16,
    fontFamily: IS_WEB ? "Common-Medium" : fontFamily.commonRegular,
  },
});
